import React from "react";
import { motion } from "framer-motion";
import {
  Sprout,
  Truck,
  Users2,
  Ban,
  GraduationCap,
  Gavel,
  ArrowUpRight,
} from "lucide-react";

const methods = [
  {
    id: "01",
    icon: Sprout,
    title: "Segregate at Source",
    span: "lg:col-span-7",
    minH: "min-h-[360px]",
    tone: "clay",
    bullets: [
      "Two bins at home: green for wet, blue for dry.",
      "Shops, hotels & religious places — mandatory segregation.",
      "Hand over only to door-to-door collection vehicles.",
    ],
    image:
      "https://images.unsplash.com/photo-1633931767470-4bec0e1ad860?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    id: "02",
    icon: Truck,
    title: "Door-to-Door Collection",
    span: "lg:col-span-5",
    minH: "min-h-[360px]",
    tone: "dark",
    bullets: [
      "Daily collection of segregated waste from every household.",
      "GPS-tracked, covered vehicles — zero transit littering.",
      "Report missed pick-ups to the MMC helpline instantly.",
    ],
  },
  {
    id: "03",
    icon: Users2,
    title: "Community Participation",
    span: "lg:col-span-4",
    minH: "min-h-[320px]",
    tone: "sand",
    bullets: [
      "Join a Ward Swachhata Mitra group.",
      "'One Day · One Hour' mass shramdaan events.",
      "Adopt a street, park or circle.",
    ],
  },
  {
    id: "04",
    icon: Ban,
    title: "Plastic-Free Morbi",
    span: "lg:col-span-8",
    minH: "min-h-[320px]",
    tone: "forest",
    bullets: [
      "Say no to single-use plastic bags, cutlery and straws.",
      "Carry cloth bags; use steel or glass bottles.",
      "Support plastic exchange drives in markets.",
    ],
    image:
      "https://images.unsplash.com/photo-1766763548613-bcb77505fc7b?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    id: "05",
    icon: GraduationCap,
    title: "School & College Drives",
    span: "lg:col-span-6",
    minH: "min-h-[300px]",
    tone: "sand",
    bullets: [
      "Rallies, poster contests, street plays.",
      "Students adopt nearby areas for weekly clean-ups.",
      "Invite sanitation workers to share their stories.",
    ],
  },
  {
    id: "06",
    icon: Gavel,
    title: "Enforce & Reward",
    span: "lg:col-span-6",
    minH: "min-h-[300px]",
    tone: "clay",
    bullets: [
      "Strict fines for littering, spitting and open dumping.",
      "Quarterly rewards for cleanest wards, streets, shops.",
      "Report garbage spots via the Swachhata App.",
    ],
  },
];

const toneClass = (t) => {
  switch (t) {
    case "clay":
      return "bg-[color:var(--mati-clay)] text-[color:var(--mati-sand)]";
    case "dark":
      return "bg-[color:var(--mati-forest)] text-[color:var(--mati-sand)]";
    case "forest":
      return "bg-[color:var(--mati-emerald)] text-[color:var(--mati-sand)]";
    case "sand":
    default:
      return "bg-white text-[color:var(--mati-ink)] border border-[color:var(--mati-border)]";
  }
};

const bulletTone = (t) =>
  t === "sand" ? "text-[color:var(--mati-forest)]" : "text-[color:var(--mati-sand)]";

export const MethodsBento = () => {
  return (
    <section
      id="methods"
      className="relative py-28 sm:py-36 bg-[color:var(--mati-sand)]"
      data-testid="methods-section"
    >
      <div className="max-w-[1360px] mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-12 gap-6 lg:gap-10 items-end mb-16">
          <div className="col-span-12 lg:col-span-7">
            <div className="eyebrow text-[color:var(--mati-clay)]">
              03 · The Citizen Playbook
            </div>
            <h2 className="font-display font-bold text-5xl sm:text-6xl lg:text-7xl text-[color:var(--mati-forest)] mt-5 leading-[0.95] tracking-tight">
              Six proven methods.{" "}
              <span className="italic text-[color:var(--mati-clay)]">
                Lasting results.
              </span>
            </h2>
          </div>
          <p className="col-span-12 lg:col-span-5 font-sub text-base sm:text-lg text-[color:var(--mati-muted)] leading-relaxed">
            Field-tested practices from Indian cities — adapted for Morbi's
            context — that deliver visible, measurable cleanliness in weeks,
            not years.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {methods.map((m, i) => {
            const Icon = m.icon;
            return (
              <motion.article
                key={m.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{
                  duration: 0.8,
                  ease: [0.16, 1, 0.3, 1],
                  delay: i * 0.06,
                }}
                className={`relative group rounded-3xl p-8 sm:p-10 flex flex-col overflow-hidden ${m.span} ${m.minH} ${toneClass(
                  m.tone
                )}`}
                data-testid={`bento-method-${m.id}`}
              >
                {m.image && (
                  <>
                    <img
                      src={m.image}
                      alt={m.title}
                      className="absolute inset-0 w-full h-full object-cover opacity-25 group-hover:opacity-35 transition-opacity"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
                  </>
                )}
                <div className="relative flex items-start justify-between">
                  <div className="font-mono-ui text-[11px] tracking-[0.3em]">
                    METHOD · {m.id}
                  </div>
                  <Icon
                    className="w-7 h-7 opacity-80 group-hover:scale-110 transition-transform"
                    strokeWidth={1.4}
                  />
                </div>
                <div className="relative mt-auto">
                  <h3 className="font-display text-3xl sm:text-4xl font-bold leading-tight tracking-tight">
                    {m.title}
                  </h3>
                  <ul className="mt-5 space-y-2.5">
                    {m.bullets.map((b, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-3 font-sub text-sm sm:text-[15px] leading-relaxed opacity-95"
                      >
                        <ArrowUpRight
                          className={`w-4 h-4 mt-0.5 shrink-0 ${bulletTone(
                            m.tone
                          )}`}
                        />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default MethodsBento;
